import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Loader2, ShieldCheck } from 'lucide-react';

import {
  authAPI,
  clearMicrosoftSSOCorrelation,
  getMicrosoftSSOCorrelation,
} from '../services/api';
import { useAuth } from '../utils/AuthContext';

function isPlatformAdminUser(user) {
  return (
    user?.role === 'PLATFORM_ADMIN' ||
    user?.is_superuser === true ||
    user?.is_staff === true
  );
}

function readInitialExchangeCode() {
  const fragmentParams = new URLSearchParams(
    window.location.hash.replace(/^#/, ''),
  );
  return fragmentParams.get('code') || '';
}

export default function MicrosoftSSOCompletePage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const codeRef = useRef(null);
  const completionPromiseRef = useRef(null);

  if (codeRef.current === null) {
    codeRef.current = readInitialExchangeCode();
  }

  const [error, setError] = useState('');
  const code = codeRef.current;

  useEffect(() => {
    let active = true;

    if (!completionPromiseRef.current) {
      completionPromiseRef.current = (async () => {
        if (!code) {
          throw new Error('Microsoft sign-in session code is missing.');
        }

        // The one-time exchange ticket is delivered in a URL fragment so it is
        // never sent to the web server. Remove it before any network operation
        // so refresh, back/forward navigation, screenshots, and copied URLs
        // cannot expose or replay it.
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname || '/sso/microsoft/success',
        );

        const browserCorrelation = getMicrosoftSSOCorrelation();
        if (!browserCorrelation) {
          throw new Error(
            'Microsoft sign-in browser verification is missing. Please sign in again.',
          );
        }

        try {
          const { data } = await authAPI.exchangeMicrosoftSSO(
            code,
            browserCorrelation,
          );
          const loggedInUser = await login(data.user, data.tokens);
          return { data, loggedInUser };
        } finally {
          clearMicrosoftSSOCorrelation();
        }
      })();
    }

    completionPromiseRef.current
      .then(({ data, loggedInUser }) => {
        if (!active) return;

        navigate(
          data.next_url ||
            (isPlatformAdminUser(loggedInUser)
              ? '/platform-admin/onboarding-requests'
              : '/dashboard'),
          { replace: true },
        );
      })
      .catch((err) => {
        if (!active) return;

        setError(
          err?.response?.data?.detail ||
            err?.response?.data?.error ||
            err?.message ||
            'Microsoft sign-in could not be completed.',
        );
      });

    return () => {
      active = false;
    };
  }, [code, login, navigate]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
        <div className="w-full max-w-md rounded-3xl border border-red-200 bg-white p-6 text-center shadow-sm">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-red-50 text-red-600">
            <AlertCircle className="h-6 w-6" />
          </div>

          <h1 className="mt-4 text-xl font-black text-slate-950">
            Microsoft sign-in failed
          </h1>

          <p className="mt-2 text-sm leading-6 text-slate-600">{error}</p>

          <button
            type="button"
            onClick={() => navigate('/login', { replace: true })}
            className="mt-6 rounded-xl bg-orange-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-orange-700"
          >
            Back to login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-6 text-center shadow-sm">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-orange-50 text-orange-600">
          <ShieldCheck className="h-6 w-6" />
        </div>

        <h1 className="mt-4 text-xl font-black text-slate-950">
          Completing Microsoft sign-in
        </h1>

        <p className="mt-2 text-sm leading-6 text-slate-600">
          Please wait while we verify your organization access.
        </p>

        <div className="mt-6 flex items-center justify-center gap-2 text-sm font-semibold text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin text-orange-600" />
          Verifying access
        </div>
      </div>
    </div>
  );
}
